/**
 * Inventory — stock snapshots and the SAP import, plus replacement planning.
 *
 * Functional spec: `qt_poc/inventory_qt.py` for the `מלאי` tab; replacement
 * planning (the `תכנון החלפה` tab) has no Qt screen behind it — see
 * `docs/features/REPLACEMENT-PLANNING.md`.
 *
 * The import is deliberately two-step: parse first (`?dry_run=true`), show the
 * user what came out including **every warning**, and only commit on
 * confirmation. The warnings are the point — they are how an operator learns
 * that a quantity could not be parsed and was set to 0. Dropping them turns a
 * half-read stock file into a silent shortage.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Button, FileInput, Group, List, Paper, Stack, Tabs, Text,
  TextInput, Textarea, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { ImportResult, InventoryLine, InventorySnapshot } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { EM_DASH, fmtDateTime, fmtQty } from '../format'
import { FILL_PANEL, FILL_REMAINING } from '../theme'
import { confirmDelete } from '../components/confirm'
import { ReplacementPlanList } from './ReplacementPlans'

export function InventoryPage() {
  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      {/* `display: flex` + `height: 100%` turns the tabs themselves into the
          flex column each tab's own content fills — without it, `Tabs.Panel`
          below is just a normal block box and `FILL_REMAINING` on it would
          have nothing to resolve a height against. `Tabs.List` is the other
          flex child; it keeps its own content height (no `flex` set), so
          whatever is left goes to whichever panel is open. */}
      <Tabs defaultValue="stock" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Tabs.List>
          <Tabs.Tab value="stock">מלאי</Tabs.Tab>
          <Tabs.Tab value="plans">תכנון החלפה</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="stock" pt="md" style={FILL_REMAINING}><StockTab /></Tabs.Panel>
        <Tabs.Panel value="plans" pt="md" style={FILL_REMAINING}><ReplacementPlanList /></Tabs.Panel>
      </Tabs>
    </Stack>
  )
}

/** Today's inventory content, unchanged — only pulled into its own component
 * so `InventoryPage` can place it inside the new `מלאי` tab rather than
 * rendering it directly. */
function StockTab() {
  const qc = useQueryClient()
  const { canEdit, canDelete } = useAuth()
  const [selected, setSelected] = useState<InventorySnapshot | null>(null)
  const [importing, setImporting] = useState(false)
  const [renaming, setRenaming] = useState<InventorySnapshot | null>(null)

  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.get<InventorySnapshot[]>('/inventory/snapshots'),
  })

  const detail = useQuery({
    queryKey: ['snapshot', selected?.id],
    queryFn: () => api.get<InventorySnapshot>(`/inventory/snapshots/${selected!.id}`),
    enabled: selected?.id != null,
  })

  async function removeSnapshot(snapshot: InventorySnapshot) {
    if (!(await confirmDelete(`הצילום "${snapshot.snapshot_name}"`))) return
    try {
      await api.delete(`/inventory/snapshots/${snapshot.id}`)
      setSelected(null)
      void qc.invalidateQueries({ queryKey: ['snapshots'] })
      notifications.show({ message: 'הצילום נמחק', color: 'green' })
    } catch (err) {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'המחיקה נכשלה',
      })
    }
  }

  const snapshotColumns = useMemo<ColumnDef<InventorySnapshot, any>[]>(
    () => [
      { accessorKey: 'snapshot_name', header: 'שם הצילום' },
      {
        accessorKey: 'imported_at',
        header: 'יובא',
        cell: (c) => fmtDateTime(c.getValue<string>()),
      },
      {
        accessorKey: 'source_file',
        header: 'קובץ מקור',
        cell: (c) => {
          const path = c.getValue<string>() || ''
          return path.split(/[\\/]/).pop() || EM_DASH
        },
      },
      {
        id: 'actions',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setRenaming(c.row.original)}
              >
                שנה שם
              </Button>
            )}
            {canDelete && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                onClick={() => void removeSnapshot(c.row.original)}
              >
                מחק
              </Button>
            )}
          </Group>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, canDelete],
  )

  const lineColumns = useMemo<ColumnDef<InventoryLine, any>[]>(
    () => [
      { accessorKey: 'sku', header: 'מק"ט יצרן', meta: { code: true } },
      {
        accessorKey: 'element_name',
        header: 'אלמנט הקפאה / פרויקט',
        // Blank means unrestricted stock — the SAP export's "available"
        // rows. Showing it as a dash rather than empty makes the distinction
        // between "available" and "missing data" visible.
        cell: (c) => c.getValue<string>() || 'זמין (ללא הקפאה)',
      },
      {
        accessorKey: 'qty_on_hand',
        header: 'כמות',
        meta: { numeric: true },
        cell: (c) => fmtQty(c.getValue<number>()),
      },
    ],
    [],
  )

  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>מלאי</Title>
        {canEdit && <Button onClick={() => setImporting(true)}>ייבוא צילום מלאי</Button>}
      </Group>

      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={snapshots.data ?? []}
          columns={snapshotColumns}
          loading={snapshots.isLoading}
          empty="אין צילומי מלאי. ייבא קובץ SAP כדי להתחיל."
          onRowClick={setSelected}
          selected={selected}
          maxHeight="100%"
        />
      </Paper>

      <Paper withBorder p="sm">
        <Group justify="space-between" mb="xs">
          <Title order={5}>
            שורות{selected ? ` — ${selected.snapshot_name}` : ''}
          </Title>
          {detail.data && (
            <Text size="sm" c="dimmed">
              {detail.data.lines.length.toLocaleString('he-IL')} שורות ·{' '}
              {new Set(detail.data.lines.map((l) => l.sku)).size.toLocaleString('he-IL')} מק"טים
            </Text>
          )}
        </Group>
        {selected ? (
          <DataGrid
            data={detail.data?.lines ?? []}
            columns={lineColumns}
            loading={detail.isLoading}
            empty="אין שורות בצילום"
            maxHeight="40vh"
          />
        ) : (
          <Text c="dimmed" size="sm">בחר צילום מלאי מהטבלה למעלה.</Text>
        )}
      </Paper>

      <ImportModal opened={importing} onClose={() => setImporting(false)} />
      <RenameSnapshotModal snapshot={renaming} onClose={() => setRenaming(null)} />
    </Stack>
  )
}

/**
 * Rename a snapshot, or change its notes.
 *
 * The header only. `InventoryDAO.update_snapshot` does not touch the lines,
 * here or on the desktop: they are what the SAP file said at import time, and
 * editing them would make a stock figure that nothing can be reconciled
 * against. Renaming exists because "Book1.xlsx" is not a name anyone can pick
 * out of a list two months later.
 */
function RenameSnapshotModal({
  snapshot, onClose,
}: { snapshot: InventorySnapshot | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const opened = snapshot !== null

  useEffect(() => {
    if (snapshot) {
      setName(snapshot.snapshot_name)
      setNotes(snapshot.notes ?? '')
      setError(null)
    }
  }, [snapshot])

  const save = useMutation({
    mutationFn: () =>
      api.patch<InventorySnapshot>(`/inventory/snapshots/${snapshot!.id}`, {
        ...snapshot, snapshot_name: name.trim(), notes,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['snapshots'] })
      void qc.invalidateQueries({ queryKey: ['snapshot'] })
      notifications.show({ message: 'הצילום עודכן', color: 'green' })
      onClose()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'העדכון נכשל.'),
  })

  return (
    <Modal opened={opened} onClose={onClose} title="שינוי שם צילום מלאי">
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) save.mutate() }}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput
          label="שם הצילום"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="הערות" autosize minRows={2} value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          שורות הצילום אינן ניתנות לעריכה — הן מה שקובץ המקור הכיל בעת הייבוא.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
            שמור
          </Button>
        </Group>
      </Stack>
      </form>
    </Modal>
  )
}

function ImportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const parse = useMutation({
    mutationFn: (f: File) => api.upload<ImportResult>('/inventory/import?dry_run=true', f),
    onSuccess: setPreview,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'קריאת הקובץ נכשלה.'),
  })

  const commit = useMutation({
    mutationFn: (f: File) => api.upload<ImportResult>('/inventory/import', f),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['snapshots'] })
      notifications.show({
        color: 'green',
        message: `נשמר צילום עם ${result.snapshot.lines.length} שורות`,
      })
      close()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת הצילום נכשלה.'),
  })

  function close() {
    setFile(null)
    setPreview(null)
    setError(null)
    onClose()
  }

  return (
    <Modal opened={opened} onClose={close} title="ייבוא צילום מלאי" size="lg">
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <FileInput
          label="קובץ מלאי"
          description="Excel (.xlsx/.xls/.xlsm) או CSV שיוצא מ-SAP"
          placeholder="בחר קובץ…"
          accept=".xlsx,.xls,.xlsm,.csv"
          value={file}
          onChange={(f) => {
            setFile(f)
            setPreview(null)
            setError(null)
          }}
        />

        {file && !preview && (
          <Button onClick={() => parse.mutate(file)} loading={parse.isPending}>
            קרא ובדוק
          </Button>
        )}

        {preview && (
          <>
            <Alert color="blue" variant="light">
              נקראו <b>{preview.snapshot.lines.length.toLocaleString('he-IL')}</b> שורות
              עבור <b>{new Set(preview.snapshot.lines.map((l) => l.sku)).size.toLocaleString('he-IL')}</b> מק"טים.
              הנתונים טרם נשמרו.
            </Alert>

            {preview.warnings.length > 0 && (
              <Alert color="yellow" variant="light" title={`${preview.warnings.length} אזהרות`}>
                <List size="sm" spacing={2}>
                  {preview.warnings.slice(0, 12).map((w, i) => (
                    <List.Item key={i}>{w}</List.Item>
                  ))}
                </List>
                {preview.warnings.length > 12 && (
                  <Text size="xs" c="dimmed" mt={4}>
                    ועוד {preview.warnings.length - 12} אזהרות…
                  </Text>
                )}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>ביטול</Button>
              <Button onClick={() => commit.mutate(file!)} loading={commit.isPending}>
                שמור צילום
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}
